import React, { useCallback } from 'react';

import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import ArrowBackIosFilled from '@mui/icons-material/ArrowBackIos';

import {
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  RecordContextProvider,
  ReferenceField,
  RichTextField,
  useGetOne,
  useRecordContext,
} from 'react-admin';

import { isArray, isPlainObject } from 'lodash';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import { ViewJSONDialog } from '../../customFields/ViewJSON';
import Pagination from '../layout/Pagination';
import useConflictContext from './useConflictContext';
import resourceNames from '../../constants/resourceNames';

const ValueField = ({ label }: {label: string}) => {
  const record = useRecordContext();

  const valueIsComplex = (isArray(record?.value) && record?.value?.every(isPlainObject))
  || isPlainObject(record?.value);

  if (!valueIsComplex) return <Typography>{record?.value ?? ''}</Typography>;

  return <ViewJSONDialog title={label} source="value" />;
};

const ConflictResolutionHistory = () => {
  const {
    searchParams,
    name,
    identificationInfo,
    entityId,
    entityType,
  } = useConflictContext();

  const urlParamsId = identificationInfo ? `${name}/${entityType}/${entityId}/${identificationInfo}` : `${name}/${entityType}/${entityId}`;
  const { data } = useGetOne('resolution-history', { id: urlParamsId });

  const navigate = useNavigate();
  const handleClose = useCallback(() => {
    navigate(`..?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  return (
    <Grid container gap={1}>
      <Grid
        mb={3}
        size={12}
      >
        <Box display="flex" alignItems="center">
          <IconButton size="medium" onClick={handleClose}>
            <ArrowBackIosFilled fontSize="medium" color="primary" />
          </IconButton>
          <Typography color="primary" variant="h5">Back</Typography>
        </Box>
      </Grid>
      <Grid size={12}>
        <Grid container>
          <Grid
            sx={{ backgroundColor: 'background.paper' }}
            size={12}
          >
            <Card
              variant="outlined"
              sx={{ m: 0 }}
            >
              <CardContent>
                <Typography variant="h5" color="primary">Resolution History</Typography>
                <RecordContextProvider value={data}>
                  <Card variant="outlined">
                    <ArrayField source="history" perPage={15}>
                      <Datagrid
                        sx={boldDataGridStyle}
                        bulkActionButtons={false}
                      >
                        <DateField source="createdAt" showTime label="Timestamp" emptyText="-" />
                        <ValueField label="Value" />
                        <RichTextField sortable={false} source="comment" emptyText="-" />
                        <ReferenceField sortable={false} label="User" source="createdBy" reference={resourceNames.MEMBERS} emptyText="System">
                          <FunctionField render={(member: {firstName: string; lastName: string}) => `${member.firstName} ${member.lastName}`} />
                        </ReferenceField>
                      </Datagrid>
                      <Pagination disableEmptyText />
                    </ArrayField>
                  </Card>
                </RecordContextProvider>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ConflictResolutionHistory;
